import { useState } from "react";
import client from "@/lib/api";
import { MapPinned, X, ShieldAlert, Loader2 } from "lucide-react";
import { t } from "./constants";

// SEC 38: One-tap Address Intelligence on every MDT assignment.
export const MdtAddressIntel = ({ address, cc }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const openIt = async () => {
    setOpen(true);
    if (data) return;
    setBusy(true);
    try { const r = await client.get("/address-intel", { params: { address } }); setData(r.data); }
    catch { setData({ error: true }); }
    setBusy(false);
  };
  if (!address) return null;
  return (
    <>
      <button data-testid="mdt-address-intel-btn" onClick={openIt}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-head font-bold rounded uppercase"
        style={{ background: "rgba(245,158,11,0.12)", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.5)" }}>
        <MapPinned size={12} /> Address Intel
      </button>
      {open && (
        <div className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div data-testid="mdt-address-intel-modal" className="w-full max-w-xl max-h-[80vh] overflow-y-auto rounded-lg border p-4"
            style={{ background: t.bg, borderColor: t.border, color: t.text }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <MapPinned size={16} className="text-[#F59E0B]" />
              <div className="font-head font-bold text-sm uppercase">Address Intelligence</div>
              <button data-testid="mdt-ai-close" onClick={() => setOpen(false)} className="ml-auto" style={{ color: t.dim }}><X size={16} /></button>
            </div>
            <div className="font-mono text-xs mb-2">{address}{cc ? ` · ${cc}` : ""}</div>
            {busy && <Loader2 className="animate-spin text-[#F59E0B]" />}
            {data?.error && <div className="font-mono text-xs text-[#EF4444]">Address Intelligence unavailable — proceed per dispatch information.</div>}
            {data && !data.error && !data.disabled && (
              <div className="space-y-3">
                <div className="font-mono text-[10px]" style={{ color: t.dim }}>{data.master?.aid} · {data.total_calls} total call(s) at this location</div>
                {data.premise_hazards?.length > 0 && (
                  <div className="border rounded p-2 flex items-start gap-2" style={{ borderColor: "#EF444466", background: "rgba(239,68,68,0.08)" }}>
                    <ShieldAlert size={14} className="text-[#EF4444] mt-0.5 shrink-0" />
                    <div className="font-mono text-[11px] text-[#EF4444] font-bold">HAZARDS: {data.premise_hazards.map((h) => h.label || h.type || h).join(" · ")}</div>
                  </div>)}
                {data.violent_recent?.length > 0 && (
                  <div className="font-mono text-[11px] text-[#EF4444] font-bold">⚠ {data.violent_recent.length} VIOLENT/WEAPONS CALL(S) LAST 12 MONTHS</div>)}
                <div className="grid grid-cols-4 gap-1.5">
                  {Object.entries(data.counts || {}).map(([k, v]) => (
                    <div key={k} className="border rounded px-2 py-1 text-center" style={{ borderColor: t.border }}>
                      <div className="font-mono text-[8px] uppercase" style={{ color: t.dim }}>{k}</div>
                      <div className="font-mono text-sm font-bold">{v}</div>
                    </div>))}
                </div>
                {data.by_type?.length > 0 && <div className="font-mono text-[10px]" style={{ color: t.dim }}>TOP TYPES: {data.by_type.slice(0, 4).map(([ty, n]) => `${ty} (${n})`).join(" · ")}</div>}
                <div className="border rounded max-h-40 overflow-y-auto" style={{ borderColor: t.border }}>
                  {(data.visits || []).filter((v) => v.cc !== cc).slice(0, 10).map((v) => (
                    <div key={v.cc} className="flex items-center gap-2 px-2 py-1.5 border-b text-[10px] font-mono" style={{ borderColor: t.border }}>
                      <span className="font-bold text-[#22D3EE]">{v.cc}</span>
                      <span className="truncate">{v.type}</span>
                      <span className="ml-auto shrink-0" style={{ color: t.dim }}>{v.units?.slice(0, 2).join(",") || "—"} · {v.ts ? new Date(v.ts).toLocaleDateString("en-GB") : ""}</span>
                    </div>))}
                  {(data.visits || []).filter((v) => v.cc !== cc).length === 0 && <div className="p-2 font-mono text-[10px]" style={{ color: t.dim }}>No prior visits on record.</div>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
